import {
  getCubaREST,
  getPropertyInfo,
  isDateProperty,
  isDateTimeProperty,
  isTimeProperty,
  isToManyRelation,
  isToOneRelation,
  MainStore,
  WithId,
} from "@cuba-platform/react";
import Notification from "./Notification/Notification";
import { AbstractDictionary } from "../../cuba/entities/base/AbstractDictionary";
import { AbstractBprocRequest } from "../../cuba/entities/base/AbstractBprocRequest";
import { AssignedPerformancePlan } from "../../cuba/entities/base/tsadv$AssignedPerformancePlan";
import { PersonExt } from "../../cuba/entities/base/base$PersonExt";
import { History } from "history";
import { toJS } from "mobx";
import moment__default from "moment";
import { SerializedEntity } from "@cuba-platform/rest";
import { UcoSignSignature } from "../../cuba/entities/base/ucosign$Signature";
import { rootStore } from "../store";
import { $api } from "app/shared/api";

export const isMobileCheck = () => window.innerWidth < 768;
export const getBlobUrl = (fileId: string): Promise<string> => {
  return getCubaREST()!.getFile(fileId).then(responseBlob => URL.createObjectURL(responseBlob));
};

export const getFileUrl = (fileId: string): string => {
  return `${getCubaREST()!.apiUrl}v2/files/${fileId}?access_token=${getCubaREST()!.restApiToken}`;
};

export const getVideoStreamUrl = (fileId: string): string => {
  const apiUrl = getCubaREST()!.apiUrl;
  return apiUrl.replace(/\/rest\/?$/, '/dispatch/video/stream/') + fileId;
};

export const downloadFile = (fileId: string, fileName: string, extension: string, fileNotFoundMessage: string) => {
  return getCubaREST()!.getFile(fileId)
    .then(value => {
      if (extension) return saveFile(value, `${fileName}.${extension}`, fileId, extension);
      return saveFile(value, `${fileName}`, fileId, extension);
    })
    .catch(reason => {
      Notification.error({
        message: fileNotFoundMessage,
      });
      throw reason;
    });
};

export const downloadFileRaw = async (
  fileId: string,
  fileNotFoundMessage: string,
) => {
  try {
    const response = await $api.get(`files/${fileId}`, {
      responseType: "blob",
    });

    if (response.status !== 200) {
      throw new Error("File not found");
    }

    const contentDisposition = response.headers["content-disposition"];
    let fileName = "downloaded_file";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].replace(/['"]/g, ""));
      }
    }

    return saveFile(response.data, fileName, fileId, null);
  } catch (error) {
    Notification.error({
      message: fileNotFoundMessage,
    });
    throw error;
  }
};

function base64ToBlob(base64: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray]);
}

export const saveBase64File = (base64: string, fileNameWithExtension: string) => {
  const blob = base64ToBlob(base64);
  saveFile(blob, fileNameWithExtension);
};

export const saveFile = (value: Blob, fileNameWithExtension: string, fileId?: string, extension?: string) => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const rawUrl = getCubaREST()!.apiUrl;
  const baseUrl = rawUrl.replace(/\/kms\/rest\/?$/, "");
  const fileUrl = `${baseUrl}/blob/${extension}/${fileId}`;

  if (!fileId && !extension && isMobileCheck()) return console.warn("url -not found");
  const anchor = document.createElement("a");
  anchor.href = isIOS ? fileUrl : URL.createObjectURL(value);
  anchor.target = "_blank";
  anchor.download = fileNameWithExtension;
  console.log(anchor);
  anchor.click();
};

export const openPdfInNewTab = async (
  fileId: string,
  fileName: string,
  fileNotFoundMessage: string,
) => {
  return getCubaREST()!.getFile(fileId)
    .then((value: Blob) => {
      const anchor = document.createElement("a");
      anchor.href = URL.createObjectURL(value);
      anchor.target = "_blank";
      anchor.name = fileName;

      anchor.click();
    }).catch(reason => {
      Notification.error({
        message: fileNotFoundMessage,
      });

      throw reason;
    });
};


export const link = (entityName: string) => {
  const link = entityName!.substring(Math.max(entityName!.indexOf("$"), entityName!.indexOf("_")) + 1);
  return "/" + link.charAt(0).toLocaleLowerCase() + link.substring(1);
};

export const wrapSrcBase64 = (url: string) => {
  return "data:image/png;base64, " + url;
};

export const dicValue = (dictionary?: AbstractDictionary, lang?: string) => {
  return dictionary && lang && dictionary[langValue(lang)];
};

export const langValue = (lang: string) => {
  lang = lang.toLocaleLowerCase();
  switch (lang) {
    case "ru":
      return "langValue1";
    case "en":
      return "langValue3";
    case "kz":
      return "langValue2";
  }
  return "langValue1";
};

export const getLocaleName = (localName: string) => {
  switch (localName) {
    case "en" : {
      return "En";
    }
    case "kz" : {
      return "Kz";
    }
    case "ru": {
      return "Ru";
    }
    default: {
      return "";
    }
  }
};

export const getBusinessKey = (request: AbstractBprocRequest) => {
  if (request instanceof AssignedPerformancePlan || request["_entityName"] === AssignedPerformancePlan.NAME)
    return request.id + "/" + (request["stage"] && request["stage"]["code"]);
  return request.id;
};

export const getFullName = (person: PersonExt, lang: string): string => {
  lang = lang.toLocaleLowerCase();
  if (lang === "en") return person.lastNameLatin + " " + person.middleNameLatin + " " + person.firstNameLatin;
  return person.lastName + " " + person.middleName + " " + person.firstName;
};

export const catchException = (promise: Promise<any>): Promise<any> => {
  return promise.catch(async (response: any) => {
    const reader = response.response.body.getReader();

    let receivedLength = 0;
    const chunks = [];
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      chunks.push(value);
      receivedLength += value.length;
    }

    const chunksAll = new Uint8Array(receivedLength);
    let position = 0;
    for (const chunk of chunks) {
      chunksAll.set(chunk, position);
      position += chunk.length;
    }

    const result = new TextDecoder("utf-8").decode(chunksAll);
    const parse = JSON.parse(result);
    throw new Error(parse.message);
  });
};

export const isNumber = (number: any): boolean => {
  return number !== undefined && number !== null && !isNaN(number);
};

export const isEquals = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a === undefined) return false;
  if (b === undefined) return false;
  return (a["id"] || b["id"]) !== undefined && a["id"] === b["id"];
};

export const goBackOrHomePage = (history: History, forceHome = false) => {
  if (forceHome) {
    history.push("/");
    return;
  }

  if (history.action === "POP") {
    history.push("/");
  } else {
    history.goBack();
  }
};


export const parseUrlForUserCode = (data: UcoSignSignature) => {
  if (!data.dataURL) return "";
  const parts = data.dataURL.split("/");
  return parts[parts.length - 1];
};
export const capitalizeFirstLetter = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};


export function setItemPropertiesValue<T extends SerializedEntity<any>>(
  mainStore: MainStore,
  entityName: string,
  item: T,
  entityPatch: any,
) {
  const metadata = toJS(mainStore.metadata)!;
  const normalizedPatch = Object.assign({}, entityPatch);


  if (typeof moment__default?.isMoment !== "function") {
    console.warn("⚠️ moment__default.isMoment не является функцией!", moment__default);
  }

  Object.entries(entityPatch).forEach(([key, value]) => {

    try {
      const propInfo = getPropertyInfo(metadata, entityName, key);

      if (propInfo && isToOneRelation(propInfo) && typeof value === "string") {
        normalizedPatch[key] = { id: value };
      }

      if (propInfo && isToManyRelation(propInfo) && Array.isArray(value)) {
        normalizedPatch[key] = value.map(id => ({ id }));
      }

      if (propInfo && isDateProperty(propInfo)) {
        if (moment__default.isMoment(value)) {
          normalizedPatch[key] = value.format("YYYY-MM-DD");
        }
      }

      if (propInfo && isTimeProperty(propInfo)) {
        if (moment__default.isMoment(value)) {
          normalizedPatch[key] = value.format("HH:mm:ss");
        }
      }

      if (propInfo && isDateTimeProperty(propInfo)) {
        if (moment__default.isMoment(value)) {
          normalizedPatch[key] = value.format("YYYY-MM-DD HH:mm:ss.SSS");
        }
      }

      if (value === "" || value == null) {
        normalizedPatch[key] = null;
      }

    } catch (error) {
      console.error(
        `[setItemPropertiesValue] ❌ Ошибка при обработке ключа "${key}" у сущности "${entityName}"`,
        error,
        { key, value, entityPatch },
      );
      normalizedPatch[key] = null;
    }
  });


  return { ...item, ...normalizedPatch };
}

export function handleSelect<T extends SerializedEntity<any>>(selected: T[], selectedRowsIds: string[] | number[], updateSelect: T[]) {
  // @ts-ignore
  selected = selected.filter(item => selectedRowsIds.includes(item.id));

  return [
    ...selected,
    ...updateSelect.filter(item => !selected.find(selectedItem => selectedItem.id === item.id)),
  ];
}

export const goBack = (history: History<unknown>) => {
  history.goBack();
};

const checkStatus = (request: any) => {
  const status = {
    isDraft: request?.status?.code === "DRAFT" || request?.status?.code === "TO_BE_REVISED",
    isApproving: request?.status?.code === "APPROVING",
    isApproved: request?.status?.code === "APPROVED",
  };
  return status;
};
const checkIsEditableUser = (request: any, isDraft: boolean) => {
  if (!isDraft) return false;
  return rootStore.userInfo.personGroupId === request?.personGroup?.id;
};
const checkIsManager = (personGroupId: string, managerInfo: any[]) => {
  if (!personGroupId) return false;
  const filteredManager = managerInfo.filter(item => item.id === rootStore.userInfo.personGroupId);
  return !(filteredManager.length < 1);
};
const checkManagerEditing = (isApproving: boolean, isManager: boolean) => {
  return isManager && isApproving;
};

type getStatusAvailableArgs = {
  request: any;
  personGroupId: string;
  managerInfo: any[];
}

const checkAllEditing = (isApproved: boolean, isManagerEditing: boolean, isEditableUser: boolean, isManager: boolean) => {
  if (isApproved) return false;
  if (isManager && isManagerEditing) return true;
  return isEditableUser;

};
export const getStatusAvailable = (args: getStatusAvailableArgs) => {
  const { request, personGroupId, managerInfo } = args;
  const allStatus = checkStatus(request);
  const isDraft = allStatus.isDraft;

  const isManager = checkIsManager(personGroupId, managerInfo);
  const isManagerEditing = checkManagerEditing(allStatus.isApproving, isManager);
  const isEditableUser = checkIsEditableUser(request, isDraft);
  const isEditing = checkAllEditing(allStatus.isApproved, isManagerEditing, isEditableUser, isManager);

  return {
    isDraft,
    allStatus,
    isManager,
    isEditableUser,
    isManagerEditing,
    isEditing,
  };
};

type typeLang = "ru" | "en" | "kz"

export const getLocaleNumber = () => {
  const lang = rootStore?.userInfo?.locale as typeLang || "ru";
  const langLower = lang.toLocaleLowerCase();
  switch (langLower) {
    case "ru":
      return "1";
    case "en":
      return "3";
    case "kz":
      return "2";
  }
  return "1";
};


export const getLocaleCode = () => {
  const lang = rootStore?.userInfo?.locale as typeLang || "ru";
  const langLower = lang.toLocaleLowerCase();
  switch (langLower) {
    case "ru":
      return "Rus";
    case "en":
      return "Eng";
    case "kz":
      return "Kz";
  }
  return "Rus";
};

export function makeCollectionContainer<T extends WithId>(
  items: (SerializedEntity<T> | T)[] = [],
): { items: SerializedEntity<T>[]; status: "DONE" } {
  return {
    items: items as SerializedEntity<T>[],
    status: "DONE",
  };
}
